import { Form, Formik, FormikHelpers } from 'formik';
import React, { useMemo } from 'react';
import { Row, Col, FormGroup, FormLabel, Modal } from 'react-bootstrap';
import * as Yup from 'yup';
import Input from 'components/common/form/input/Input';
import { useFindUserRolesQuery } from 'service/userQuery/userQuery';
import Select from 'components/common/form/select/Select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import {
  useInviteUserMutation,
  useUpdateUserInviteMutation
} from 'service/authQuery/authQuery';
import Button from 'components/base/Button';
import { useToast } from 'hooks/useToast';
import { User } from 'pages/users/users.type';

type FormValue = {
  id?: string;
  username: string;
  email: string;
  roles: string;
};

interface Props {
  show: boolean;
  onClose: () => void;
  data: User | null;
}

const validationSchema = Yup.object({
  username: Yup.string().required('Full name is required'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email address is required'),
  roles: Yup.string().required('Role is required')
});

const InviteUserModal = ({ show, onClose, data }: Props) => {
  const { data: roles } = useFindUserRolesQuery('');
  const [inviteUser, { isLoading: saveUserLoading }] = useInviteUserMutation();
  const [updateUserInvite, { isLoading: updateUserLoading }] =
    useUpdateUserInviteMutation();
  const { open } = useToast();

  const isEdit = data !== null;

  const handleSubmit = (
    values: FormValue,
    { resetForm }: FormikHelpers<FormValue>
  ) => {
    const payload = {
      ...values,
      roles: [values.roles]
    };
    if (isEdit && data) {
      payload.id = data._id;
    }
    const userHandler = isEdit ? updateUserInvite : inviteUser;
    userHandler(payload)
      .unwrap()
      .then(response => {
        open(response?.message, 'success');
        resetForm();
        onClose();
      })
      .catch(error => {
        console.log(error);
        const message = error?.data?.error || 'Something went wrong';
        open(message, 'error');
      });
  };

  const userRoles = useMemo(() => {
    return Array.isArray(roles)
      ? [{ label: 'Select', value: '', desc: '' }].concat(
          roles.map(({ name: label, _id: value, desc }) => ({
            label,
            value,
            desc
          }))
        )
      : [];
  }, [roles]);

  return (
    <Modal show={show} onHide={onClose} className="p-0" size="lg" centered>
      <Modal.Body className="p-4">
        <Formik
          initialValues={{
            username: data?.username || '',
            roles: data?.roles?.[0]?._id || '',
            email: data?.email || ''
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values }) => {
            const role = userRoles.find(
              ({ value }) => values['roles'] === value
            );
            return (
              <Form>
                <Row className="g-5">
                  <Col xs={12}>
                    <h5>{isEdit ? 'Update Invitation' : 'Invite new user'} </h5>
                  </Col>
                  <Col xs={12} lg={6}>
                    <FormGroup className="text-start">
                      <FormLabel htmlFor="username">Full name*</FormLabel>
                      <Input name="username" id="username" />
                    </FormGroup>
                  </Col>
                  <Col xs={12} lg={6}>
                    <FormGroup className="text-start">
                      <FormLabel htmlFor="email">Email*</FormLabel>
                      <Input name="email" id="email" disabled={isEdit} />
                    </FormGroup>
                  </Col>
                  <Col lg={12}>
                    <h6 className="mb-3">Roles</h6>
                    <FormGroup className="text-start">
                      <FormLabel htmlFor="roles">Roles*</FormLabel>
                      <Select name="roles" id="roles" options={userRoles} />
                    </FormGroup>
                    {role && role.desc && (
                      <p className="text-xs mb-0 mt-2 text-primary">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                        {role.desc}
                      </p>
                    )}
                  </Col>
                </Row>
                <Button
                  variant="primary"
                  className="mt-6"
                  type="submit"
                  loading={saveUserLoading || updateUserLoading}
                  loadingPosition="start"
                >
                  {isEdit ? 'Update' : 'Invite user'}
                </Button>
              </Form>
            );
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default InviteUserModal;
